<template>
  <SideSheet
    ref="sheet"
    :is-open="props.isOpen"
    position="right"
    @close="handleSheetClose"
    @scroll="handleScroll"
  >
    <ProductAdditionalInfo v-if="isOpenAdditionalInfo && additionalInfo" :additional-info="additionalInfo" @close-additional-info="isOpenAdditionalInfo = false" />
    <div v-else class="sheet" data-testid="sheet">
      <ProductSideSheetBar
        :color="props.color"
        :show-header-text="showHeaderText"
        :hero="hero"
        :stop-investment-bar-open="stopInvestmentBarOpen"
        @close-stop-investment-bar="stopInvestmentBarOpen = false"
        @close="handleSheetClose"
      />
      <ProductSideSheetHero
        :color="props.color"
        :loading="isLoadingDetails"
        :hero="hero"
        :error="hasErrorDetails"
        @retry="fetchDetails"
      />
      <ProductSideSheetActionBar
        v-if="!isLoadingDetails && !hasErrorDetails"
        :product-api-id="productApiId"
        :color="props.color"
        :loading="isLoadingDetails"
        :category-selected-product="props.categorySelectedProduct"
        :stop-investment="shouldStopInvestmentWarning"
        :hero="hero"
        :error="hasErrorDetails"
        @open-stop-investment-bar="stopInvestmentBarOpen = true"
        @add-product="$emit('add-product', productApiId)"
        @remove-product="$emit('remove-product', productApiId)"
      />
      <ProductPerformance
        v-if="props.categorySelectedProduct === EProductTypes.FUNDS"
        :product-api-id="productApiId"
        :category-selected-product="props.categorySelectedProduct"
      />
      <ProductSideSheetDetails
        :details="detailSections"
        :costs="costsDetails"
        :loading="isLoadingDetails"
        :error="hasErrorDetails"
        @retry="fetchDetails"
      />
      <ProductSideSheetFooter
        v-if="!isLoadingDetails && !hasErrorDetails"
        :should-render-additional-info="Boolean(additionalInfo)"
        :footer="detailFooter"
        :footer-actions="detailFooterActions"
        @open-additional-info="isOpenAdditionalInfo = true"
      />
    </div>
  </SideSheet>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed, PropType } from 'vue';
import {
  getCatalogProductDetails,
  getProductAdditionalInfo,
  IProductDetailFooter,
  IProductDetailsSection,
  IProductHero,
  IProductCostsDetails,
  IProductDetailFooterAction,
  IProductAdditionalInfo
} from '@/modules/catalog/services/products';
import { EProductTypes } from '@/modules/catalog/services/products/products-types';
import { SideSheet } from '@/modules/common/components/SideSheet';
import ProductSideSheetBar from './components/ProductSideSheetBar/ProductSideSheetBar.vue';
import ProductSideSheetHero from './components/ProductSideSheetHero/ProductSideSheetHero.vue';
import ProductSideSheetActionBar from './components/ProductSideSheetActionBar/ProductSideSheetActionBar.vue';
import ProductSideSheetFooter from './components/ProductSideSheetFooter/ProductSideSheetFooter.vue';
import ProductSideSheetDetails from './components/ProductSideSheetDetails/ProductSideSheetDetails.vue';
import ProductPerformance from './components/ProductPerformance/ProductPerformance.vue';
import ProductAdditionalInfo from './components/ProductAdditionalInfo/ProductAdditionalInfo.vue';

const showHeaderText = ref(false);
const sheet = ref<HTMLElement | null>(null);
const hero = ref<IProductHero>();
const isLoadingDetails = ref(false);
const hasErrorDetails = ref(false);
const detailSections = ref<IProductDetailsSection[]>();
const detailFooter = ref<IProductDetailFooter[]>();
const detailFooterActions = ref<IProductDetailFooterAction[] | null>(null);
const stopInvestmentBarOpen = ref(false);
const costsDetails = ref<IProductCostsDetails>();
const isOpenAdditionalInfo = ref(false);
const additionalInfo = ref<IProductAdditionalInfo | null>(null);

const props = defineProps({
  isOpen: Boolean,
  isBuilder: {
    type: Boolean,
    default: false
  },
  canAdd: {
    type: Boolean,
    default: false
  },
  categorySelectedProduct: {
    type: String as PropType<EProductTypes>,
    default: EProductTypes.FIXED_INCOME
  },
  productApiId: {
    type: String,
    default: ''
  },
  color: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['close', 'add-product', 'remove-product']);

const shouldStopInvestmentWarning = computed(() => {
  return !!hero.value?.warnings;
});

const handleScroll = (scrollTop: number) => {
  if (scrollTop > 130) {
    showHeaderText.value = true;
  } else {
    showHeaderText.value = false;
  }
};

const handleSheetClose = () => {
  emit('close');
  showHeaderText.value = false;
  stopInvestmentBarOpen.value = false;
  hero.value = {} as IProductHero;
  detailSections.value = [];
  detailFooter.value = [];
  costsDetails.value = undefined;
  isOpenAdditionalInfo.value = false;
};

const fetchDetails = async () => {
  isLoadingDetails.value = true;
  hasErrorDetails.value = false;
  try {
    const productDetails = await getCatalogProductDetails(
      props.productApiId,
      props.categorySelectedProduct,
      props.isBuilder,
      props.canAdd
    );
    detailSections.value = productDetails.details.sections;
    detailFooter.value = productDetails.details.footer;
    detailFooterActions.value = productDetails.details.footerActions || null;
    hero.value = productDetails.hero;
    costsDetails.value = productDetails.details.costsDetails;
  } catch (error) {
    hasErrorDetails.value = true;
  } finally {
    isLoadingDetails.value = false;
  }
};

const fetchAdditionalInfo = async () => {
  try {
    const additionalInfoResponse = await getProductAdditionalInfo();
    additionalInfo.value = additionalInfoResponse;
  } catch (error) {
    console.error('Error fetching additional info', error);
  }
};

watch(
  () => props.isOpen,
  newVal => {
    if (newVal) {
      fetchDetails();
    }
  }
);

onMounted(() => {
  fetchAdditionalInfo();
  if (props.isOpen) {
    fetchDetails();
  }
});
</script>

<style lang="less" scoped>
.sheet {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  min-height: 100vh;
  background: @background-secondary;

  &__wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: space-between;
    justify-content: space-between;
  }
}

:deep(.panel-content.right) {
  width: 100%;
  @media screen and (min-width: 768px) {
    width: 683px;
  }
}

:deep(.container-right) {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

:deep(.row-item > div) {
  padding-top: @size-spacing-x500;
}
</style>
